// Spring-coupled chain for tails, ears, and other secondary motion.
//
// Verlet integration with distance constraints. Each node tracks its
// position and previous position; velocity is inferred as (pos - prev),
// implicitly damped each frame. After integration, a few passes of
// distance constraint relaxation re-pin neighbouring nodes to the
// resting segment length — pinning the root to the supplied anchor.
//
// Two optional knobs make the chain expressive enough for a squirrel
// tail without bolting on a tail-specific module:
//
//   - `dampingPerNode` — a base-stiff/tip-floppy gradient. Anchored
//     base, whippy tip is the canonical squirrel tail feel.
//   - `restTargets` + `restWeight` — a soft pull toward a per-node
//     target shape, layered on top of the spring dynamics. Lets the
//     tail "carry" itself in a controlled pose at rest while still
//     reacting dynamically to body motion.
//
// References: subprotocol/verlet-js, the Caliko/Cyclone family of
// constraint-solver tutorials. Hand-rolled rather than wrapping `wiggle`
// to keep the Vec3-tuple pattern consistent with the other animation
// modules and avoid a dependency on a library that assumes fixed
// gravity (we will eventually want surface-relative gravity).

import type { Vec3 } from './ik';

export interface SpringChainState {
  /** World-space node positions; [0] is pinned to the anchor each frame. */
  positions: Array<[number, number, number]>;
  /** Verlet "previous" positions, used to infer velocity. */
  prevPositions: Array<[number, number, number]>;
}

export interface SpringChainParams {
  /** Resting distance between consecutive nodes. */
  segmentLength: number;
  /** Number of distance-constraint relaxation passes (3–6 typical). */
  iterations: number;
  /** Velocity damping per frame, 0..1. 0 = no damping, ~0.95 = stiff. */
  damping: number;
  /**
   * Optional per-node damping. Index 0 is the pinned root (unused). When
   * provided, takes precedence over the scalar `damping`. Lets a chain
   * be stiff at the base and floppy at the tip — the squirrel tail
   * shape, where powerful root muscles anchor the base while the tip
   * whips freely.
   */
  dampingPerNode?: readonly number[];
  /** Per-frame acceleration (gravity, drag bias, etc.) in world space. */
  acceleration: Vec3;
  /**
   * Optional per-node target positions. With `restWeight > 0`, each
   * non-root node is pulled toward its target each frame. Lets a chain
   * settle to an authored shape (squirrel up-and-over tail carry,
   * rabbit ear droop) while still reacting dynamically to body motion.
   */
  restTargets?: readonly Vec3[];
  /**
   * Per-frame pull strength toward `restTargets`. 0 = pure spring (the
   * tail just trails the anchor); 1 = rigid (snaps every frame); 0.05
   * to 0.15 reads as "lightly carried." Ignored when `restTargets` is
   * absent.
   */
  restWeight?: number;
}

export function makeSpringChain(initial: readonly Vec3[]): SpringChainState {
  return {
    positions: initial.map(p => [p[0], p[1], p[2]] as [number, number, number]),
    prevPositions: initial.map(p => [p[0], p[1], p[2]] as [number, number, number]),
  };
}

export function updateSpringChain(
  state: SpringChainState,
  rootPos: Vec3,
  dt: number,
  params: SpringChainParams,
): void {
  const n = state.positions.length;
  if (n === 0) return;

  // Pin the root, both current and previous, so velocity at root is zero.
  state.positions[0][0] = rootPos[0];
  state.positions[0][1] = rootPos[1];
  state.positions[0][2] = rootPos[2];
  state.prevPositions[0][0] = rootPos[0];
  state.prevPositions[0][1] = rootPos[1];
  state.prevPositions[0][2] = rootPos[2];

  // Verlet integrate each non-root node.
  const ax = params.acceleration[0] * dt * dt;
  const ay = params.acceleration[1] * dt * dt;
  const az = params.acceleration[2] * dt * dt;
  const scalarDampVel = 1 - params.damping;
  const perNode = params.dampingPerNode;
  const restTargets = params.restTargets;
  const restWeight = params.restWeight ?? 0;
  const usingRest = restTargets !== undefined && restWeight > 0;

  for (let i = 1; i < n; i++) {
    const dampVel = perNode !== undefined ? 1 - perNode[i] : scalarDampVel;
    const p = state.positions[i];
    const prev = state.prevPositions[i];
    const px = p[0];
    const py = p[1];
    const pz = p[2];
    let vx = (p[0] - prev[0]) * dampVel + ax;
    let vy = (p[1] - prev[1]) * dampVel + ay;
    let vz = (p[2] - prev[2]) * dampVel + az;

    if (usingRest) {
      const t = restTargets[i];
      vx += (t[0] - p[0]) * restWeight;
      vy += (t[1] - p[1]) * restWeight;
      vz += (t[2] - p[2]) * restWeight;
    }

    prev[0] = px;
    prev[1] = py;
    prev[2] = pz;
    p[0] += vx;
    p[1] += vy;
    p[2] += vz;
  }

  // Relax distance constraints. Root is fixed; everything else shares
  // the correction with its parent.
  for (let iter = 0; iter < params.iterations; iter++) {
    for (let i = 1; i < n; i++) {
      const a = state.positions[i - 1];
      const b = state.positions[i];
      const dx = b[0] - a[0];
      const dy = b[1] - a[1];
      const dz = b[2] - a[2];
      const d = Math.sqrt(dx * dx + dy * dy + dz * dz);
      if (d < 1e-9) continue;
      const k = (d - params.segmentLength) / d;
      if (i === 1) {
        // Root is pinned — full correction goes to b.
        b[0] -= dx * k;
        b[1] -= dy * k;
        b[2] -= dz * k;
      } else {
        a[0] += dx * k * 0.5;
        a[1] += dy * k * 0.5;
        a[2] += dz * k * 0.5;
        b[0] -= dx * k * 0.5;
        b[1] -= dy * k * 0.5;
        b[2] -= dz * k * 0.5;
      }
    }
  }
}
